/**
 * M2 onboarding: Name + EIN section — EIN lookup (loading / not found / found) and Legal name + EIN fields.
 */

import React, { useState } from 'react';
import { formatEIN, validateEIN, INPUT_CLASS } from './orgSetupUtils';

const PencilButton = ({ onClick, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    className="shrink-0 p-1 rounded text-ink-muted hover:text-ink hover:bg-paper-muted"
    aria-label={ariaLabel}
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  </button>
);

export default function OrgNameAndEIN({
  status,
  payload,
  ein,
  legalName,
  onEINChange,
  onLegalNameChange,
}) {
  const [editingField, setEditingField] = useState(null);

  if (status === 'loading') {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-4 bg-paper-muted rounded w-3/4" />
        <div className="h-4 bg-paper-muted rounded w-1/2" />
        <div className="h-4 bg-paper-muted rounded w-2/3" />
      </div>
    );
  }

  if (status === 'not_found' || status === 'error') {
    return (
      <div className="space-y-3">
        <p className="text-base text-ink-secondary">
          {status === 'not_found' ? "We couldn't find a match. Please enter your EIN below to verify your nonprofit." : 'Lookup failed. Please enter your EIN manually.'}
        </p>
        <div className="space-y-1.5">
          <label className="block text-base font-medium text-ink">EIN</label>
          <input
            type="text"
            value={ein}
            onChange={(e) => onEINChange(e.target.value)}
            placeholder="12-3456789"
            className={INPUT_CLASS}
          />
        </div>
      </div>
    );
  }

  if (status === 'found' && payload) {
    const displayName = legalName ?? payload.legalName ?? '';
    const displayEin = ein ?? payload.ein ?? '';

    return (
      <div className="space-y-6">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <label className="text-base font-medium text-ink-secondary">Legal name</label>
            {onLegalNameChange && (
              <PencilButton
                onClick={() => setEditingField(editingField === 'legalName' ? null : 'legalName')}
                ariaLabel="Edit legal name"
              />
            )}
          </div>
          {editingField === 'legalName' && onLegalNameChange ? (
            <input
              type="text"
              value={displayName}
              onChange={(e) => onLegalNameChange(e.target.value)}
              className={INPUT_CLASS}
              autoFocus
            />
          ) : (
            <p className="text-sm text-ink">{displayName || '—'}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <label className="text-base font-medium text-ink-secondary">EIN</label>
            {onEINChange && (
              <PencilButton
                onClick={() => setEditingField(editingField === 'ein' ? null : 'ein')}
                ariaLabel="Edit EIN"
              />
            )}
          </div>
          {editingField === 'ein' && onEINChange ? (
            <input
              type="text"
              value={displayEin}
              onChange={(e) => onEINChange(e.target.value)}
              placeholder="12-3456789"
              className={INPUT_CLASS}
              autoFocus
            />
          ) : (
            <p className="text-sm text-ink">{displayEin ? formatEIN(displayEin) : '—'}</p>
          )}
        </div>
      </div>
    );
  }

  return null;
}
